import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { guid } from '@fry/lib/utils';

import { EditorEligibilityTree } from './editor-eligibility-tree.model';
import { EditorEligibilityTreeService } from './editor-eligibility-tree.service';
import {
  EditorEligibilityOperatorWithRule
} from '../editor-eligibility-operator-with-rule/editor-eligibility-operator-with-rule.interface';
import { EditorEligibility } from '../editor-eligibility/editor-eligibility.model';


@Injectable()
export class EditorEligibilityTreeStore {
  private readonly _eligibility$ = new BehaviorSubject<EditorEligibility>(null);
  private readonly _eligibilityTree$ = new BehaviorSubject<EditorEligibilityTree>(null);
  private readonly _editingOperatorWithRule$ = new BehaviorSubject<EditorEligibilityOperatorWithRule>(null);

  readonly eligibility$ = this._eligibility$.asObservable();
  readonly eligibilityTree$ = this._eligibilityTree$.asObservable();
  readonly editingOperatorWithRule$ = this._editingOperatorWithRule$.asObservable();

  constructor(
    private eligibilityTreeService: EditorEligibilityTreeService
  ) {

  }

  private get eligibility(): EditorEligibility {
    return this._eligibility$.getValue();
  }

  private set eligibility(value: EditorEligibility) {
    this._eligibility$.next(value);
  }

  private get eligibilityTree(): EditorEligibilityTree {
    return this._eligibilityTree$.getValue();
  }

  private set eligibilityTree(value: EditorEligibilityTree) {
    this._eligibilityTree$.next(value);
  }

  private get editingOperatorWithRule(): EditorEligibilityOperatorWithRule {
    return this._editingOperatorWithRule$.getValue();
  }

  private set editingOperatorWithRule(value: EditorEligibilityOperatorWithRule) {
    this._editingOperatorWithRule$.next(value);
  }

  initialize(eligibilityTree: EditorEligibilityTree) {
    this.eligibilityTree = eligibilityTree;
  }

  initializeEmpty() {
    this.eligibility = { id: guid(), name: null, rules: '' };
    this.initialize(this.eligibilityTreeService.getEmptyEligibilityTree());
  }

  initializeFromEligibility(eligibility: EditorEligibility) {
    this.eligibility = eligibility;
    this.initialize(this.eligibilityTreeService.getEligibilityTreeFromEligibility(eligibility));
  }

  initializeFromDuplicatingEligibility(eligibility: EditorEligibility) {
    this.eligibility = { id: guid(), name: eligibility.name, rules: eligibility.rules };
    const eligibilityTree = this.eligibilityTreeService.getEligibilityTreeFromEligibility(eligibility);
    eligibilityTree.rootOperator.name = `Copy of ${eligibilityTree.rootOperator.name}`;
    this.initialize(eligibilityTree);
  }

  getEligibility(): EditorEligibility {
    return this.eligibilityTreeService.getEligibilityFromEligibilityTree(
      this.eligibility,
      this.eligibilityTree
    );
  }

  terminate() {
    this.stopEditing();
    this.eligibilityTree = null;
    this.eligibility = null;
  }

  stopEditing() {
    this.stopEditingOperatorWithRule();
  }

  startEditingOperatorWithRule(operatorId: string) {
    const curEligibilityTree = this.eligibilityTree;
    const operator = curEligibilityTree.rootOperator.getOperatorById(operatorId);
    this.editingOperatorWithRule = {
      operator,
      rule: operator.hasRule() ? curEligibilityTree.ruleList[operator.ruleIndex] : null
    };
  }

  stopEditingOperatorWithRule() {
    this.editingOperatorWithRule = null;
  }

  addEmptyRuleAfter(operatorId: string) {
    this.eligibilityTree = this.eligibilityTree.immutableAddEmptyRuleAfter(operatorId);
  }

  bisectOperator(operatorId: string) {
    this.eligibilityTree = this.eligibilityTree.immutableMakeCompositeOperator(operatorId);
  }

  replaceOperatorWithRule(operatorId: string, replacingOperatorWithRule: EditorEligibilityOperatorWithRule) {
    this.eligibilityTree = this.eligibilityTree.immutableReplaceOperatorWithRule(operatorId, replacingOperatorWithRule);
  }

  moveOperatorUp(operatorId: string) {
    this.eligibilityTree = this.eligibilityTree.immutableOperatorMoveUp(operatorId);
  }

  moveOperatorDown(operatorId: string) {
    this.eligibilityTree = this.eligibilityTree.immutableOperatorMoveDown(operatorId);
  }

  duplicateBranch(operatorId: string) {
    this.eligibilityTree = this.eligibilityTree.immutableDuplicateBranch(operatorId);
  }

  deleteOperator(operatorId: string) {
    this.eligibilityTree = this.eligibilityTree.immutableDeleteOperator(operatorId);
  }
}
